import { useState, useRef, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';
import { Banner, Card, Field, Modal, ModalHeader, PageHeader, Stat, Tag, inputStyle, useToast } from '@/components/ui';
import { Select } from '@/components/Select';
import { Icon, I } from '@/components/icons';

type BtnAction = 'capture_phone' | 'uri' | 'tag' | 'goto';
interface FunnelBtn { label: string; action: BtnAction; value: string }
interface FunnelNode { id: string; title: string; desc?: string; imageUrl?: string; price?: string; buttons?: FunnelBtn[] }
interface Funnel { id: string; name: string; keyword: string; active: boolean; tagOnEnter: string | null; nodes: FunnelNode[] }
interface FunnelStats { impressions: number; clicks: number; leads: number; clickRate: number; leadRate: number }

const ACTIONS = [
  { value: 'capture_phone', label: '留電話（自動建檔綁定）' },
  { value: 'goto', label: '跳到另一張卡' },
  { value: 'uri', label: '開啟連結' },
  { value: 'tag', label: '貼標籤（分眾）' },
];
const newId = () => (crypto.randomUUID?.() ?? `n${Math.floor(performance.now())}`).slice(0, 8);

export function LineFunnelPage() {
  const qc = useQueryClient();
  const { msg, ok, fail } = useToast();
  const [editing, setEditing] = useState<Funnel | null>(null);
  const [creating, setCreating] = useState(false);

  const funnels = useQuery({ queryKey: ['funnels'], queryFn: () => api.get<Funnel[]>('/engagement/funnels') });
  const list = funnels.data ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: ['funnels'] });

  const toggle = useMutation({
    mutationFn: (f: Funnel) => api.patch(`/engagement/funnels/${f.id}`, { ...f, active: !f.active }),
    onSuccess: (_d, f) => { ok(f.active ? '已停用漏斗' : '已啟用漏斗'); refresh(); },
    onError: fail,
  });
  const del = useMutation({
    mutationFn: (id: string) => api.delete(`/engagement/funnels/${id}`),
    onSuccess: () => { ok('已刪除漏斗'); refresh(); },
    onError: fail,
  });

  return (
    <div>
      <PageHeader
        title="LINE 銷售漏斗"
        subtitle="設定「關鍵字 → 圖卡 → 按鈕跳卡 / 留電話建檔」。客人主動觸發，回覆走免費 reply，不吃推播額度。"
        right={<button className="btn btn-primary" onClick={() => setCreating(true)}><Icon d={I.plus} size={13} /> 新增漏斗</button>}
      />
      <Banner msg={msg} />

      <div style={{ display: 'flex', gap: 14, marginBottom: 16 }}>
        <Stat label="漏斗總數" value={String(list.length)} tone="primary" />
        <Stat label="啟用中" value={String(list.filter((f) => f.active).length)} tone="tag" />
      </div>

      <RichMenuLibrary funnels={list} onMsg={(t) => ok(t)} onFail={fail} />

      <Card title="我的漏斗" icon={<Icon d={I.trend} size={14} style={{ color: 'var(--primary)' }} />}>
        {list.length === 0 && <div style={{ padding: 16, fontSize: 13, color: 'var(--ink-4)' }}>尚無漏斗。點右上「新增漏斗」，設定客人傳什麼關鍵字、回什麼圖卡。</div>}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {list.map((f) => (
            <div key={f.id} style={{ padding: '12px 14px', border: '1px solid var(--line)', borderRadius: 10, opacity: f.active ? 1 : 0.6 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 8 }}>
                    {f.name}
                    {f.active ? <Tag tone="tag">啟用中</Tag> : <Tag tone="ink">已停用</Tag>}
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 2 }}>
                    關鍵字「<b className="num">{f.keyword}</b>」→ {f.nodes.length} 張卡
                    {f.tagOnEnter && <> · 標籤 <Tag tone="primary">{f.tagOnEnter}</Tag></>}
                  </div>
                </div>
                <button className="btn btn-ghost" style={btn} onClick={() => setEditing(f)}><Icon d={I.edit} size={12} /> 編輯</button>
                <button className="btn btn-ghost" style={btn} disabled={toggle.isPending} onClick={() => toggle.mutate(f)}>{f.active ? '停用' : '啟用'}</button>
                <button className="btn btn-ghost" style={{ ...btn, color: 'var(--danger)' }} disabled={del.isPending} onClick={() => { if (confirm(`刪除漏斗「${f.name}」？`)) del.mutate(f.id); }}>刪除</button>
              </div>
              <FunnelStatsBar funnelId={f.id} />
            </div>
          ))}
        </div>
      </Card>

      {(creating || editing) && (
        <FunnelEditor
          funnel={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { ok('漏斗已儲存'); setCreating(false); setEditing(null); refresh(); }}
          onFail={fail}
        />
      )}
    </div>
  );
}

const btn = { padding: '5px 10px', fontSize: 11 } as const;

/* ───── 轉換數據條：曝光 → 點按 → 留資 ───── */
function FunnelStatsBar({ funnelId }: { funnelId: string }) {
  const stats = useQuery({ queryKey: ['funnel-stats', funnelId], queryFn: () => api.get<FunnelStats>(`/engagement/funnels/${funnelId}/stats`) });
  const s = stats.data;
  if (!s) return null;
  const Step = ({ label, value, rate }: { label: string; value: number; rate?: string }) => (
    <div style={{ flex: 1, textAlign: 'center', padding: '6px 4px', background: 'var(--surface-2)', borderRadius: 8 }}>
      <div style={{ fontSize: 10, color: 'var(--ink-4)' }}>{label}</div>
      <div className="num" style={{ fontSize: 18, fontWeight: 700, color: 'var(--ink)' }}>{value}</div>
      {rate && <div style={{ fontSize: 10, color: 'var(--primary)' }}>{rate}</div>}
    </div>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 10 }}>
      <Step label="圖卡曝光" value={s.impressions} />
      <span style={{ color: 'var(--ink-4)' }}>›</span>
      <Step label="點按按鈕" value={s.clicks} rate={`${s.clickRate}%`} />
      <span style={{ color: 'var(--ink-4)' }}>›</span>
      <Step label="留資建檔" value={s.leads} rate={`${s.leadRate}%`} />
    </div>
  );
}

/* ───── 多卡片編輯器 ───── */
function FunnelEditor({ funnel, onClose, onSaved, onFail }: { funnel: Funnel | null; onClose: () => void; onSaved: () => void; onFail: (e: unknown) => void }) {
  const [meta, setMeta] = useState({ name: funnel?.name ?? '', keyword: funnel?.keyword ?? '', tagOnEnter: funnel?.tagOnEnter ?? '' });
  const [nodes, setNodes] = useState<FunnelNode[]>(
    funnel?.nodes?.length ? funnel.nodes.map((n) => ({ ...n, buttons: n.buttons ?? [] })) : [{ id: newId(), title: '', buttons: [{ label: '我要諮詢', action: 'capture_phone', value: '' }] }],
  );
  const [idx, setIdx] = useState(0);
  const node = nodes[idx];

  const setMetaK = (k: keyof typeof meta, v: string) => setMeta((s) => ({ ...s, [k]: v }));
  const patchNode = (patch: Partial<FunnelNode>) => setNodes((ns) => ns.map((n, j) => (j === idx ? { ...n, ...patch } : n)));
  const buttons = node.buttons ?? [];
  const setBtn = (i: number, patch: Partial<FunnelBtn>) => patchNode({ buttons: buttons.map((b, j) => (j === i ? { ...b, ...patch } : b)) });
  const addBtn = () => patchNode({ buttons: [...buttons, { label: '', action: 'tag', value: '' }] });
  const rmBtn = (i: number) => patchNode({ buttons: buttons.filter((_, j) => j !== i) });
  const addCard = () => { const n = { id: newId(), title: '', buttons: [] as FunnelBtn[] }; setNodes((ns) => [...ns, n]); setIdx(nodes.length); };
  const rmCard = () => { if (nodes.length <= 1) return; setNodes((ns) => ns.filter((_, j) => j !== idx)); setIdx(0); };

  const gotoOptions = nodes.map((n, j) => ({ value: n.id, label: `卡 ${j + 1}：${n.title || '(未命名)'}` }));

  const save = useMutation({
    mutationFn: () => {
      const clean = nodes.map((n) => ({ ...n, buttons: (n.buttons ?? []).filter((b) => b.label.trim()) }));
      const payload = { name: meta.name, keyword: meta.keyword, tagOnEnter: meta.tagOnEnter || undefined, active: funnel?.active ?? true, nodes: clean };
      return funnel ? api.patch(`/engagement/funnels/${funnel.id}`, payload) : api.post('/engagement/funnels', payload);
    },
    onSuccess: onSaved,
    onError: onFail,
  });

  const ready = meta.name.trim() && meta.keyword.trim() && nodes[0]?.title.trim();

  return (
    <Modal open onClose={onClose} width={820}>
      <ModalHeader title={funnel ? '編輯漏斗' : '新增漏斗'} sub="第 1 張卡是關鍵字觸發時送出的；按鈕可「跳到另一張卡」串成多步流程" onClose={onClose} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 0 }}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12, borderRight: '1px solid var(--line)', maxHeight: 540, overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
            <Field label="漏斗名稱 *"><input style={inputStyle} value={meta.name} onChange={(e) => setMetaK('name', e.target.value)} placeholder="皮秒諮詢" /></Field>
            <Field label="觸發關鍵字 *"><input style={inputStyle} value={meta.keyword} onChange={(e) => setMetaK('keyword', e.target.value)} placeholder="預約" /></Field>
          </div>
          <Field label="進入標籤（選填，自動分眾）"><input style={inputStyle} value={meta.tagOnEnter} onChange={(e) => setMetaK('tagOnEnter', e.target.value)} placeholder="預約意向" /></Field>

          {/* 卡片切換 */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center', borderTop: '1px solid var(--line)', paddingTop: 10 }}>
            {nodes.map((n, j) => (
              <button key={n.id} type="button" onClick={() => setIdx(j)} className="btn btn-ghost" style={{ ...btn, background: j === idx ? 'var(--primary-tint)' : undefined, color: j === idx ? 'var(--primary-deep)' : undefined, fontWeight: j === idx ? 700 : 500 }}>
                卡 {j + 1}{j === 0 ? '（首卡）' : ''}
              </button>
            ))}
            <button type="button" className="btn btn-ghost" style={btn} onClick={addCard}><Icon d={I.plus} size={11} /> 加卡片</button>
            {nodes.length > 1 && <button type="button" className="btn btn-ghost" style={{ ...btn, color: 'var(--danger)' }} onClick={rmCard}>刪此卡</button>}
          </div>

          <Field label="標題 *"><input style={inputStyle} value={node.title} onChange={(e) => patchNode({ title: e.target.value })} placeholder="熱門療程・皮秒雷射" /></Field>
          <Field label="圖片網址（選填）"><input style={inputStyle} className="num" value={node.imageUrl ?? ''} onChange={(e) => patchNode({ imageUrl: e.target.value })} placeholder="https://.../pico.jpg" /></Field>
          <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
            <Field label="說明（選填）"><input style={inputStyle} value={node.desc ?? ''} onChange={(e) => patchNode({ desc: e.target.value })} placeholder="淨膚亮白・改善毛孔" /></Field>
            <Field label="價格（選填）"><input style={inputStyle} className="num" value={node.price ?? ''} onChange={(e) => patchNode({ price: e.target.value })} placeholder="12000" /></Field>
          </div>

          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginTop: 4 }}>按鈕（客人點了會怎樣）</div>
          {buttons.map((b, i) => (
            <div key={i} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', gap: 8 }}>
                <input style={{ ...inputStyle, flex: 1 }} value={b.label} onChange={(e) => setBtn(i, { label: e.target.value })} placeholder="按鈕文字（我要諮詢）" />
                <div style={{ width: 190 }}><Select value={b.action} onChange={(v) => setBtn(i, { action: v as BtnAction, value: '' })} options={ACTIONS} /></div>
                {buttons.length > 1 && <button className="btn btn-ghost" style={btn} onClick={() => rmBtn(i)}>×</button>}
              </div>
              {b.action === 'goto' && <div style={{ }}><Select value={b.value} onChange={(v) => setBtn(i, { value: v })} placeholder="選要跳去的卡片" options={gotoOptions.filter((o) => o.value !== node.id)} /></div>}
              {b.action === 'uri' && <input style={inputStyle} value={b.value} onChange={(e) => setBtn(i, { value: e.target.value })} placeholder="https://..." />}
              {b.action === 'tag' && <input style={inputStyle} value={b.value} onChange={(e) => setBtn(i, { value: e.target.value })} placeholder="標籤文字（例：皮秒意向）" />}
              {b.action === 'capture_phone' && <div style={{ fontSize: 11, color: 'var(--ink-4)' }}>客人按下 → 系統請他留電話 → 自動建檔並綁定 LINE</div>}
            </div>
          ))}
          {buttons.length < 4 && <button className="btn btn-ghost" style={{ ...btn, alignSelf: 'flex-start' }} onClick={addBtn}><Icon d={I.plus} size={12} /> 加按鈕</button>}
        </div>

        {/* 即時預覽 */}
        <div style={{ padding: 20, background: 'var(--surface-2)' }}>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 8 }}>卡 {idx + 1} 在 LINE 的樣子</div>
          <div style={{ borderRadius: 12, overflow: 'hidden', background: '#fff', border: '1px solid var(--line)', boxShadow: '0 4px 14px rgba(0,0,0,0.08)' }}>
            {node.imageUrl ? <img src={node.imageUrl} alt="" style={{ width: '100%', height: 130, objectFit: 'cover', display: 'block' }} /> : <div style={{ height: 90, background: 'var(--surface-3)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-4)', fontSize: 12 }}>（無圖片）</div>}
            <div style={{ padding: 12 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{node.title || '圖卡標題'}</div>
              {node.desc && <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 4 }}>{node.desc}</div>}
              {node.price && <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: 17, marginTop: 6 }}>NT${node.price}</div>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 10 }}>
                {buttons.filter((b) => b.label.trim()).map((b, i) => (
                  <div key={i} style={{ textAlign: 'center', padding: '7px 0', borderRadius: 6, background: 'var(--primary)', color: '#fff', fontSize: 12.5, fontWeight: 600 }}>{b.label}</div>
                ))}
              </div>
            </div>
          </div>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 10 }}>觸發：客人傳含「{meta.keyword || '？'}」的訊息</div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!ready || save.isPending} onClick={() => save.mutate()}><Icon d={I.check} size={13} /> 儲存漏斗</button>
      </div>
    </Modal>
  );
}



/* ───── 圖文選單庫（多選單 + 分頁切換） ───── */
const GRID: Record<number, [number, number]> = { 2: [2, 1], 3: [3, 1], 4: [2, 2], 6: [3, 2] };
const PALETTE = ['#B97A65', '#A56B85', '#6B8AAB', '#7A9B7A', '#C99F3D', '#8B5A48'];
const RW = 2500, RH = 1686;
type Cell = { label: string; color: string; action: 'funnel' | 'uri' | 'switch'; funnelId: string; uri: string; switchTo: string };
const DEFAULT_CELLS: Cell[] = PALETTE.map((color, i) => ({ label: ['預約', '熱門療程', '活動優惠', '真人客服', '會員', '門市資訊'][i], color, action: 'funnel', funnelId: '', uri: '', switchTo: '' }));
interface MenuConfig { count: number; chatBarText: string; bgMode: 'generated' | 'upload'; tabbed?: boolean; cells: Cell[]; imageBase64?: string }
interface RichMenu { id: string; name: string; isDefault: boolean; config: MenuConfig; lineRichMenuId: string | null }

function RichMenuLibrary({ funnels, onMsg, onFail }: { funnels: Funnel[]; onMsg: (t: string) => void; onFail: (e: unknown) => void }) {
  const qc = useQueryClient();
  const menus = useQuery({ queryKey: ['richmenus'], queryFn: () => api.get<RichMenu[]>('/engagement/richmenus') });
  const [editing, setEditing] = useState<RichMenu | null>(null);
  const [creating, setCreating] = useState(false);
  const list = menus.data ?? [];
  const refresh = () => void qc.invalidateQueries({ queryKey: ['richmenus'] });
  const published = list.some((m) => m.lineRichMenuId);

  const setDefault = useMutation({ mutationFn: (id: string) => api.post(`/engagement/richmenus/${id}/default`), onSuccess: () => { onMsg('已設為預設選單'); refresh(); }, onError: onFail });
  const del = useMutation({ mutationFn: (id: string) => api.delete(`/engagement/richmenus/${id}`), onSuccess: () => { onMsg('已刪除選單'); refresh(); }, onError: onFail });
  const publish = useMutation({ mutationFn: () => api.post<{ published: number }>('/engagement/richmenus/publish'), onSuccess: (r) => { onMsg(`已發布 ${r.published} 套選單到 LINE（重開聊天室可見）`); refresh(); }, onError: onFail });
  const unpublish = useMutation({ mutationFn: () => api.post('/engagement/richmenus/unpublish'), onSuccess: () => { onMsg('已下架圖文選單'); refresh(); }, onError: onFail });

  return (
    <Card title="LINE 圖文選單庫（多選單・分頁切換）" icon={<Icon d={I.line} size={14} style={{ color: '#06C755' }} />}
      right={<span style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        {published ? <Tag tone="tag">已發布</Tag> : <Tag tone="ink">未發布</Tag>}
        <button className="btn btn-ghost" style={btn} onClick={() => setCreating(true)}><Icon d={I.plus} size={12} /> 新增選單</button>
      </span>}>
      <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
        存多套選單，設一套為「預設」（客人初始看到）。格子動作可「觸發漏斗 / 開連結 / 切換到另一套選單」（分頁式）。設好按「發布」整組推上 LINE。
      </div>
      {list.length === 0 && <div style={{ fontSize: 13, color: 'var(--ink-4)', padding: 12 }}>尚無選單。點右上「新增選單」。</div>}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map((m) => (
          <div key={m.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px', border: '1px solid var(--line)', borderRadius: 10 }}>
            {m.config?.imageBase64
              ? <img src={m.config.imageBase64} alt="" style={{ width: 80, height: 54, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--line)' }} />
              : <div style={{ width: 80, height: 54, borderRadius: 6, background: 'var(--surface-3)', flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, display: 'flex', gap: 8, alignItems: 'center' }}>{m.name}{m.isDefault && <Tag tone="primary">預設</Tag>}{m.lineRichMenuId && <Tag tone="tag">已上線</Tag>}</div>
              <div style={{ fontSize: 11, color: 'var(--ink-4)', marginTop: 2 }}>{m.config?.count ?? 0} 格 · 選單列「{m.config?.chatBarText || '選單'}」</div>
            </div>
            {!m.isDefault && <button className="btn btn-ghost" style={btn} disabled={setDefault.isPending} onClick={() => setDefault.mutate(m.id)}>設為預設</button>}
            <button className="btn btn-ghost" style={btn} onClick={() => setEditing(m)}><Icon d={I.edit} size={12} /> 編輯</button>
            <button className="btn btn-ghost" style={{ ...btn, color: 'var(--danger)' }} disabled={del.isPending} onClick={() => { if (confirm(`刪除選單「${m.name}」？`)) del.mutate(m.id); }}>刪除</button>
          </div>
        ))}
      </div>
      {list.length > 0 && (
        <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
          <button className="btn btn-primary" disabled={publish.isPending} onClick={() => publish.mutate()}><Icon d={I.check} size={13} /> 發布全部到 LINE</button>
          {published && <button className="btn btn-ghost" disabled={unpublish.isPending} onClick={() => unpublish.mutate()}>下架</button>}
        </div>
      )}
      {(creating || editing) && (
        <MenuEditorModal menu={editing} allMenus={list} funnels={funnels}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={() => { setCreating(false); setEditing(null); refresh(); onMsg('選單已儲存'); }}
          onFail={onFail} />
      )}
    </Card>
  );
}

function MenuEditorModal({ menu, allMenus, funnels, onClose, onSaved, onFail }: { menu: RichMenu | null; allMenus: RichMenu[]; funnels: Funnel[]; onClose: () => void; onSaved: () => void; onFail: (e: unknown) => void }) {
  const cfg = menu?.config;
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const bgImgRef = useRef<HTMLImageElement | null>(null);
  const [name, setName] = useState(menu?.name ?? '');
  const [count, setCount] = useState(cfg?.count ?? 4);
  const [chatBarText, setChatBarText] = useState(cfg?.chatBarText ?? '服務選單');
  const [bgMode, setBgMode] = useState<'generated' | 'upload'>(cfg?.bgMode ?? 'generated');
  const [bgImage, setBgImage] = useState(cfg?.bgMode === 'upload' ? cfg?.imageBase64 ?? '' : '');
  const [cells, setCells] = useState<Cell[]>(cfg?.cells?.length ? cfg.cells.map((c) => ({ ...c })) : DEFAULT_CELLS);
  const [tabbed, setTabbed] = useState(cfg?.tabbed ?? false);
  const [imgv, setImgv] = useState(0);
  const visible = cells.slice(0, count);
  const otherMenus = allMenus.filter((m) => m.id !== menu?.id);

  useEffect(() => {
    if (bgMode === 'upload' && bgImage && !bgImgRef.current) {
      const img = new Image();
      img.onload = () => { bgImgRef.current = img; setImgv((v) => v + 1); };
      img.src = bgImage;
    }
  }, [bgMode, bgImage]);

  useEffect(() => {
    const cv = canvasRef.current; if (!cv) return;
    const ctx = cv.getContext('2d'); if (!ctx) return;
    ctx.clearRect(0, 0, RW, RH);
    const font = (px: number) => `bold ${px}px "PingFang TC","Microsoft JhengHei",sans-serif`;
    const tabH = tabbed ? RH * 0.2 : 0;
    // 分頁列（顯示所有層的名字，目前層highlight）
    if (tabbed) {
      const tabs = menu ? allMenus.map((m) => ({ id: m.id, name: m.name })) : [...allMenus.map((m) => ({ id: m.id, name: m.name })), { id: '__new__', name }];
      const N = Math.max(tabs.length, 1);
      const tw = RW / N;
      tabs.forEach((t, i) => {
        const active = t.id === (menu?.id ?? '__new__');
        ctx.fillStyle = active ? '#8B5A48' : '#CDB6AB';
        ctx.fillRect(i * tw, 0, tw, tabH);
        ctx.strokeStyle = 'rgba(255,255,255,0.6)'; ctx.lineWidth = 5; ctx.strokeRect(i * tw, 0, tw, tabH);
        ctx.fillStyle = active ? '#fff' : 'rgba(255,255,255,0.85)';
        ctx.font = font(Math.round(tabH * 0.36)); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(t.name || '(未命名)', i * tw + tw / 2, tabH / 2);
      });
    }
    // 內容區
    const [cols, rows] = GRID[count];
    const cw = RW / cols, ch = (RH - tabH) / rows;
    if (bgMode === 'upload' && bgImgRef.current) {
      const img = bgImgRef.current, aw = RW, ah = RH - tabH;
      const scale = Math.max(aw / img.width, ah / img.height);
      ctx.save(); ctx.beginPath(); ctx.rect(0, tabH, aw, ah); ctx.clip();
      ctx.drawImage(img, (aw - img.width * scale) / 2, tabH + (ah - img.height * scale) / 2, img.width * scale, img.height * scale);
      ctx.restore();
    } else {
      visible.forEach((c, i) => {
        const col = i % cols, row = Math.floor(i / cols);
        const x = col * cw, y = tabH + row * ch;
        ctx.fillStyle = c.color; ctx.fillRect(x, y, cw, ch);
        ctx.strokeStyle = 'rgba(255,255,255,0.65)'; ctx.lineWidth = 8; ctx.strokeRect(x, y, cw, ch);
        ctx.fillStyle = '#fff'; ctx.font = font(Math.round(ch * 0.2));
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(c.label || '（未填）', x + cw / 2, y + ch / 2);
      });
    }
  }, [count, cells, bgMode, imgv, tabbed, name, allMenus, menu]);

  const setCell = (i: number, patch: Partial<Cell>) => setCells((cs) => cs.map((c, j) => (j === i ? { ...c, ...patch } : c)));
  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; if (!file) return;
    const reader = new FileReader();
    reader.onload = () => { const url = reader.result as string; const img = new Image(); img.onload = () => { bgImgRef.current = img; setBgImage(url); setBgMode('upload'); setImgv((v) => v + 1); }; img.src = url; };
    reader.readAsDataURL(file);
  };

  const save = useMutation({
    mutationFn: () => {
      const imageBase64 = canvasRef.current!.toDataURL('image/jpeg', 0.85);
      const config: MenuConfig = { count, chatBarText, bgMode, tabbed, cells: cells.slice(0, count), imageBase64 };
      const body = { name, config };
      return menu ? api.patch(`/engagement/richmenus/${menu.id}`, body) : api.post('/engagement/richmenus', body);
    },
    onSuccess: onSaved, onError: onFail,
  });

  const cellReady = (c: Cell) => (c.action === 'funnel' ? !!c.funnelId : c.action === 'uri' ? !!c.uri.trim() : !!c.switchTo) && (bgMode === 'upload' || !!c.label.trim());
  const ready = name.trim() && visible.every(cellReady) && (bgMode === 'generated' || !!bgImage);

  return (
    <Modal open onClose={onClose} width={860}>
      <ModalHeader title={menu ? '編輯選單' : '新增選單'} sub="每格可觸發漏斗 / 開連結 / 切換到另一套選單（分頁式）" onClose={onClose} />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 0 }}>
        <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 8, borderRight: '1px solid var(--line)', maxHeight: 540, overflow: 'auto' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 1fr', gap: 10 }}>
            <Field label="選單名稱 *"><input style={inputStyle} value={name} onChange={(e) => setName(e.target.value)} placeholder="日常版" /></Field>
            <Field label="格數"><Select value={String(count)} onChange={(v) => setCount(Number(v))} options={[{ value: '2', label: '2 格' }, { value: '3', label: '3 格' }, { value: '4', label: '4 格' }, { value: '6', label: '6 格' }]} /></Field>
            <Field label="選單列文字"><input style={inputStyle} value={chatBarText} onChange={(e) => setChatBarText(e.target.value)} placeholder="服務選單" /></Field>
            <Field label="底圖"><Select value={bgMode} onChange={(v) => setBgMode(v as 'generated' | 'upload')} options={[{ value: 'generated', label: '系統色塊' }, { value: 'upload', label: '上傳圖' }]} /></Field>
          </div>
          <div>
            <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 6, fontWeight: 600 }}>切換樣式（這套選單怎麼跟其他選單切換）</div>
            <div style={{ display: 'flex', gap: 10 }}>
              <StyleCard selected={tabbed} onClick={() => setTabbed(true)} title="🗂️ 分頁列式" desc="上方一排 tab，列出全部選單，點任一直接跳那一層" mock="tab" />
              <StyleCard selected={!tabbed} onClick={() => setTabbed(false)} title="🔘 按鈕切換式" desc="某一格當按鈕，點了切到另一張選單（無上方 tab 列）" mock="btn" />
            </div>
          </div>
          {bgMode === 'upload' && (
            <div style={{ fontSize: 12 }}>
              <label className="btn btn-ghost" style={{ ...btn, cursor: 'pointer' }}><Icon d={I.eye} size={12} /> 選擇底圖檔<input type="file" accept="image/*" style={{ display: 'none' }} onChange={onFile} /></label>
              <span style={{ marginLeft: 8, color: bgImage ? 'var(--success)' : 'var(--ink-4)' }}>{bgImage ? '已選擇' : '建議 2500×1686'}</span>
            </div>
          )}
          {visible.map((c, i) => (
            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
              {bgMode === 'generated' && <input type="color" value={c.color} onChange={(e) => setCell(i, { color: e.target.value })} style={{ width: 30, height: 34, border: '1px solid var(--line)', borderRadius: 6, padding: 2, background: 'transparent', cursor: 'pointer', flexShrink: 0, marginBottom: 2 }} />}
              <div style={{ flex: 1 }}><Field label={`第 ${i + 1} 格`}><input style={inputStyle} value={c.label} onChange={(e) => setCell(i, { label: e.target.value })} placeholder="預約" /></Field></div>
              <div style={{ width: 100 }}><Field label="動作"><Select value={c.action} onChange={(v) => setCell(i, { action: v as Cell['action'] })} options={[{ value: 'funnel', label: '觸發漏斗' }, { value: 'uri', label: '開連結' }, { value: 'switch', label: '切換選單' }]} /></Field></div>
              <div style={{ flex: 1.3 }}>
                {c.action === 'funnel' && <Field label="哪個漏斗"><Select value={c.funnelId} onChange={(v) => setCell(i, { funnelId: v })} placeholder="選漏斗" options={funnels.map((f) => ({ value: f.id, label: `${f.name}（${f.keyword}）` }))} /></Field>}
                {c.action === 'uri' && <Field label="連結網址"><input style={inputStyle} className="num" value={c.uri} onChange={(e) => setCell(i, { uri: e.target.value })} placeholder="https://..." /></Field>}
                {c.action === 'switch' && <Field label="切換到哪套選單"><Select value={c.switchTo} onChange={(v) => setCell(i, { switchTo: v })} placeholder="選選單" options={otherMenus.map((m) => ({ value: m.id, label: m.name }))} /></Field>}
              </div>
            </div>
          ))}
          {cells.some((c) => c.action === 'switch') && otherMenus.length === 0 && <div style={{ fontSize: 11, color: 'var(--danger)' }}>「切換選單」需要先有第二套選單；請先存這套，再新增另一套。</div>}
        </div>
        <div style={{ padding: 20, background: 'var(--surface-2)' }}>
          <div style={{ fontSize: 11, color: 'var(--ink-4)', marginBottom: 6 }}>預覽</div>
          <div style={{ position: 'relative' }}>
            <canvas ref={canvasRef} width={RW} height={RH} style={{ width: '100%', borderRadius: 10, border: '1px solid var(--line)', display: 'block' }} />
            {bgMode === 'upload' && (
              <div style={{ position: 'absolute', inset: 0, display: 'grid', gridTemplateColumns: `repeat(${GRID[count][0]}, 1fr)`, gridTemplateRows: `repeat(${GRID[count][1]}, 1fr)` }}>
                {visible.map((_, i) => <div key={i} style={{ border: '1px dashed rgba(255,255,255,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 11, textShadow: '0 1px 3px rgba(0,0,0,0.6)' }}>{i + 1}</div>)}
              </div>
            )}
          </div>
        </div>
      </div>
      <div style={{ display: 'flex', gap: 8, padding: 16, borderTop: '1px solid var(--line)' }}>
        <button className="btn btn-ghost" style={{ flex: 1 }} onClick={onClose}>取消</button>
        <button className="btn btn-primary" style={{ flex: 1.5 }} disabled={!ready || save.isPending} onClick={() => save.mutate()}><Icon d={I.check} size={13} /> 儲存選單</button>
      </div>
    </Modal>
  );
}

/* ───── 切換樣式示意圖卡 ───── */
function StyleCard({ selected, onClick, title, desc, mock }: { selected: boolean; onClick: () => void; title: string; desc: string; mock: 'tab' | 'btn' }) {
  return (
    <button type="button" onClick={onClick} style={{
      flex: 1, textAlign: 'left', cursor: 'pointer', padding: 12, borderRadius: 10,
      border: `1.5px solid ${selected ? 'var(--primary)' : 'var(--line)'}`,
      background: selected ? 'var(--primary-tint)' : 'var(--surface)',
      display: 'flex', gap: 10, alignItems: 'center',
    }}>
      <div style={{ width: 60, flexShrink: 0 }}>{mock === 'tab' ? <MockTab /> : <MockBtn />}</div>
      <div>
        <div style={{ fontWeight: 700, fontSize: 13 }}>{title}</div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 2, lineHeight: 1.35 }}>{desc}</div>
      </div>
    </button>
  );
}

function MockTab() {
  return (
    <div style={{ borderRadius: 5, overflow: 'hidden', border: '1px solid var(--line)' }}>
      <div style={{ display: 'flex', height: 11 }}>
        {[0, 1, 2, 3].map((i) => <div key={i} style={{ flex: 1, background: i === 0 ? '#8B5A48' : '#CDB6AB', borderRight: i < 3 ? '1px solid #fff' : undefined }} />)}
      </div>
      <div style={{ display: 'flex', height: 29 }}>
        <div style={{ flex: 1, background: '#B97A65', borderRight: '1px solid #fff' }} />
        <div style={{ flex: 1, background: '#A56B85' }} />
      </div>
    </div>
  );
}

function MockBtn() {
  return (
    <div style={{ borderRadius: 5, overflow: 'hidden', border: '1px solid var(--line)', display: 'grid', gridTemplateColumns: '1fr 1fr', gridTemplateRows: '1fr 1fr', height: 40 }}>
      <div style={{ background: '#B97A65', borderRight: '1px solid #fff', borderBottom: '1px solid #fff' }} />
      <div style={{ background: '#A56B85', borderBottom: '1px solid #fff' }} />
      <div style={{ background: '#6B8AAB', borderRight: '1px solid #fff' }} />
      <div style={{ background: '#7A9B7A', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 13, fontWeight: 700 }}>↗</div>
    </div>
  );
}
